"use client"

import { motion } from "motion/react"
import { cn } from "@/lib/utils"

interface PageHeroProps {
  title: string
  description: string
  badge?: string
}

const ease = [0.25, 0.4, 0, 1] as const

export function PageHero({ title, description, badge }: PageHeroProps) {
  return (
    <section className="section-hero px-4 md:px-6">
      <motion.div
        className="mx-auto max-w-7xl"
        initial="hidden"
        animate="visible"
        transition={{ staggerChildren: 0.1 }}
      >
        {badge && (
          <motion.span
            className={cn(
              "inline-flex items-center rounded-full border border-primary/20 bg-primary/5",
              "px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-primary"
            )}
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.5, ease }}
          >
            {badge}
          </motion.span>
        )}
        <motion.h1
          className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl"
          style={{ textWrap: "balance" }}
          variants={{
            hidden: { opacity: 0, y: 12 },
            visible: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.6, ease }}
        >
          {title}
        </motion.h1>
        <motion.p
          className="mt-5 max-w-[60ch] text-lg leading-relaxed text-muted-foreground md:text-xl"
          variants={{
            hidden: { opacity: 0, y: 16 },
            visible: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.5, ease }}
        >
          {description}
        </motion.p>
        {/* Decorative accent line */}
        <motion.div
          className="mt-6 h-1 w-16 rounded-full bg-primary/60"
          aria-hidden="true"
          variants={{
            hidden: { opacity: 0, scaleX: 0 },
            visible: { opacity: 1, scaleX: 1 },
          }}
          transition={{ duration: 0.6, ease }}
          style={{ originX: 0 }}
        />
      </motion.div>
    </section>
  )
}
